import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ShieldCheck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BeltStrip } from "@/components/karate";

const LINKS = [
  { to: "/", label: "Home", end: true, id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/federations", label: "Federations", id: "federations" },
  { to: "/news", label: "News", id: "news" },
  { to: "/events", label: "Events", id: "events" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative py-1 text-sm font-medium transition-colors ${
      isActive ? "text-ukf-red" : "text-slate-600 hover:text-ukf-red"
    } after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:bg-ukf-gold after:transition-all ${
      isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
    }`;

  return (
    <>
      <div className="hidden border-b border-white/5 bg-ukf-obsidian text-slate-400 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-1.5 text-xs lg:px-8">
          <span className="font-mono uppercase tracking-[0.15em] text-slate-400">
            Official Website · Union of Karate Federations
          </span>
          <div className="flex items-center gap-6">
            <a href="tel:9654712772" data-testid="topbar-phone" className="flex items-center gap-1.5 transition-colors hover:text-white">
              <Phone className="h-3 w-3 text-ukf-gold" /> 9654712772
            </a>
            <a href="mailto:ukf.org.in@gmail.com" data-testid="topbar-email" className="flex items-center gap-1.5 transition-colors hover:text-white">
              <Mail className="h-3 w-3 text-ukf-gold" /> ukf.org.in@gmail.com
            </a>
            <Link to="/admin/login" data-testid="topbar-admin" className="flex items-center gap-1.5 border-l border-white/15 pl-6 transition-colors hover:text-white">
              <ShieldCheck className="h-3 w-3 text-ukf-gold" /> Admin
            </Link>
          </div>
        </div>
      </div>
    <header
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "border-slate-200 bg-white/90 shadow-sm backdrop-blur-md" : "border-transparent bg-white/70 backdrop-blur"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src="/branding/ukf-emblem.png" alt="UKF Emblem" className="h-11 w-auto" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-lg tracking-tight text-ukf-ink">UKF</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              Union of Karate Federations
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          {LINKS.map((l) => (
            <NavLink key={l.id} to={l.to} end={l.end} className={linkClass} data-testid={`nav-${l.id}-link`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-md text-slate-700 md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>
      <BeltStrip className="h-[3px]" />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-100 bg-white md:hidden"
          >
            <div className="flex flex-col px-4 py-2">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.end}
                  data-testid={`nav-mobile-${l.id}-link`}
                  className={({ isActive }) =>
                    `border-b border-slate-50 py-3 text-sm font-medium ${isActive ? "text-ukf-red" : "text-slate-700"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    </>
  );
}
